import { MaterialGroup, PartItem } from '@cutr/constants/cutlist';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useCurrentGroupGrainDirection } from '@/api/materialsGroup';
import { usePartsClipboard } from '@/api/partsClipboard';
import { useActions, useCutlistState } from '@/api/store';
import { Button } from '@/primitives/Button';
import {
  AddCircleOutline,
  Clipboard,
  Dashboard,
  Duplicate,
  Icon,
  Layer,
  Menu,
  Scissors,
} from '@/primitives/Icons';
import { useCurrentFeatures, useTicker } from '@/theme';

import { useSelectedPartsContext } from '../AgentQuotingPartTable/SelectedPartsContext';
import { CsvUploadHandler } from '../CsvCutlistUpload';

export const MaterialGroupActions = ({ group }: { group: MaterialGroup }) => {
  const { t } = useTranslation();
  const { addPart, setPart } = useActions();
  const { csvUpload, hasPartStrips, grainDirectionLevel } =
    useCurrentFeatures();
  const source = useTicker();
  const grainDirection = useCurrentGroupGrainDirection();

  const sheetPart = useCutlistState((state) =>
    state.parts.find(
      (part) => part.groupId === group.id && part.partType === 'sheet'
    )
  );

  const addFullSheet = () => {
    window.analytics.track('Cutlist Full Sheet Added', { group });
    if (!sheetPart) {
      addPart(group, 'sheet');
      return;
    }
    const updatedSheetPart = { ...sheetPart };
    updatedSheetPart['quantity'] = (updatedSheetPart.quantity || 0) + 1;
    setPart(updatedSheetPart);
  };

  const addStrip = () => {
    window.analytics.track('Cutlist Strip Added', { group });
    const partItem = addPart(group, 'strip');
    // Focus on the first input of the new part
    window.requestAnimationFrame(() =>
      (
        document.querySelector(
          `#stripRow-${partItem.id} input:first-of-type`
        ) as HTMLInputElement
      )?.focus()
    );
  };

  const onAddPart = () => {
    window.analytics.track('Cutlist Part Added', { group });

    let newPart: PartItem;
    if (grainDirectionLevel === 'group') {
      newPart = addPart(group, 'panel', grainDirection);
    } else {
      newPart = addPart(group);
    }

    // Focus on the first input of the new part
    window.requestAnimationFrame(() =>
      (
        document.querySelector(
          `#panelRow-${newPart.id} input:first-of-type`
        ) as HTMLInputElement
      )?.focus()
    );
  };

  const onAddBeam = () => {
    window.analytics.track('Cutlist Beam Added', { group });
    const partItem = addPart(group, 'beam');
    // Focus on the first input of the new part
    window.requestAnimationFrame(() =>
      (
        document.querySelector(
          `#beamRow-${partItem.id} input:first-of-type`
        ) as HTMLInputElement
      )?.focus()
    );
  };

  const addStripTooltip =
    group.sheetSizeSelection === 'automatic'
      ? t('cutlist-form.errors.automaticSheetSizeWarning.addStrip')
      : '';
  const addStripDisabled =
    !group.core1 || group.sheetSizeSelection === 'automatic';

  const addFullSheetTooltip =
    group.sheetSizeSelection === 'automatic'
      ? t('cutlist-form.errors.automaticSheetSizeWarning.addFullSheet')
      : '';
  const addFullSheetDisabled =
    !group.core1 || group.sheetSizeSelection === 'automatic';

  if (group.type === 'beams')
    return (
      <Button
        name="add-beam"
        onClick={onAddBeam}
        variant="secondary"
        icon={<Icon icon={<AddCircleOutline />} />}
      >
        {t('cutlist-form.cta.addNew')}
      </Button>
    );

  return (
    <div className="flexAlign">
      {group.type === 'panels-and-strips' && (
        <Button
          name="add-part"
          disabled={!group.core1}
          onClick={onAddPart}
          variant="straight"
          icon={<Icon icon={<Dashboard />} />}
        >
          {t('cutlist-form.cta.addPart')}
        </Button>
      )}

      {hasPartStrips && group.type === 'panels-and-strips' && (
        <Button
          name="add-strip"
          disabled={addStripDisabled}
          tooltip={addStripTooltip}
          onClick={() => addStrip()}
          variant="secondary"
          icon={<Icon icon={<Menu />} />}
        >
          {t('cutlist-form.cta.addStrip')}
        </Button>
      )}

      {group.type === 'sheets-only' && (
        <Button
          name="add-full-sheet"
          disabled={addFullSheetDisabled}
          tooltip={addFullSheetTooltip}
          onClick={() => addFullSheet()}
          variant="secondary"
          icon={<Icon icon={<Layer />} />}
        >
          {t('cutlist-form.cta.addFullSheet')}
        </Button>
      )}

      {source === 'SWISSKRONO' && <PartCopyPasteButtons group={group} />}

      {csvUpload && group.core1 && <CsvUploadHandler />}
    </div>
  );
};

const PartCopyPasteButtons = ({ group }: { group: MaterialGroup }) => {
  const { t } = useTranslation();
  const selectedPartsContext = useSelectedPartsContext();
  const { parts: clipboardParts, paste, cut, copy } = usePartsClipboard();

  if (!selectedPartsContext) {
    return null;
  }

  const { selectedParts, disableAll } = selectedPartsContext;

  return (
    <>
      <Button
        name="copy-selected-panels"
        disabled={!group.core1 || selectedParts.length === 0}
        onClick={() => {
          copy(selectedParts);
          disableAll();
          toast.success(t('agent.toasts.copiedParts'));
        }}
        variant="secondary"
        tooltip={
          selectedParts.length > 0
            ? t('agent.tooltip.copySelectedPanels')
            : t('agent.tooltip.noPartsSelected')
        }
        icon={<Icon icon={<Duplicate />} />}
      />

      <Button
        name="cut-selected-panels"
        disabled={!group.core1 || selectedParts.length === 0}
        onClick={() => {
          cut(selectedParts);
          disableAll();
          toast.success(t('agent.toasts.cutParts'));
        }}
        variant="secondary"
        tooltip={
          selectedParts.length > 0
            ? t('agent.tooltip.cutSelectedPanels')
            : t('agent.tooltip.noPartsSelected')
        }
        icon={<Icon icon={<Scissors />} />}
      />

      <Button
        name="paste-from-clipboard"
        disabled={!group.core1 || clipboardParts.length === 0}
        onClick={() => {
          paste();
          toast.success(
            t('agent.toasts.pastedParts', { count: clipboardParts.length })
          );
        }}
        variant="secondary"
        tooltip={
          clipboardParts.length > 0
            ? t('agent.tooltip.pasteFromClipboard', {
                count: clipboardParts.length,
              })
            : t('agent.tooltip.clipboardIsEmpty')
        }
        icon={<Icon icon={<Clipboard />} />}
      />
    </>
  );
};
